import React, { useEffect, useState } from "react";
import { Loader2, Users, UserPlus, MousePointerClick, Eye, Timer, Activity, FileText, Share2, Globe2, Monitor, Radio } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Kpi, Bars } from "@/components/admin/TrafficPanel";
import { Chart, DataTable, fmtDuration } from "./parts";

const n = (v) => Math.round(v || 0);

export default function AnalyticsPanel({ days }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    setD(null); setErr("");
    api.get("/admin/google/analytics", { params: { days } }).then((r) => setD(r.data)).catch((e) => setErr(formatApiError(e.response?.data?.detail)));
  }, [days]);
  if (err) return <p className="mt-4 text-sm text-amber-400" data-testid="ga-error">{err}</p>;
  if (!d) return <Loader2 className="mt-6 animate-spin text-white/30" />;
  const t = d.totals;
  return (
    <div data-testid="ga-panel">
      <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 text-emerald-300 text-sm font-semibold" data-testid="ga-realtime">
        <Radio size={14} className="animate-pulse" /> {d.realtime} active users right now
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mt-3">
        <Kpi icon={Users} label="Users" value={n(t.activeUsers)} testid="ga-users" />
        <Kpi icon={UserPlus} label="New users" value={n(t.newUsers)} testid="ga-new-users" />
        <Kpi icon={MousePointerClick} label="Sessions" value={n(t.sessions)} testid="ga-sessions" />
        <Kpi icon={Eye} label="Page views" value={n(t.screenPageViews)} testid="ga-views" />
        <Kpi icon={Timer} label="Avg. session duration" value={fmtDuration(t.averageSessionDuration || 0)} testid="ga-duration" />
        <Kpi icon={Activity} label="Engagement rate" value={`${((t.engagementRate || 0) * 100).toFixed(1)}%`} testid="ga-engagement" />
      </div>
      <Chart data={d.daily} x="date" testid="ga-chart" lines={[{ key: "activeUsers", name: "Users", color: "#34d399" }, { key: "screenPageViews", name: "Page views", color: "#fbbf24" }]} />
      <DataTable title="Visitors per page / article" icon={FileText} rows={d.pages} testid="ga-pages" cols={[
        { label: "Page", get: (r) => r.pagePath },
        { label: "Title", get: (r) => (r.pageTitle && r.pageTitle !== "(not set)" ? r.pageTitle : "—") },
        { label: "Views", num: true, get: (r) => n(r.screenPageViews).toLocaleString() },
        { label: "Users", num: true, get: (r) => n(r.activeUsers).toLocaleString() },
        { label: "Avg. time", num: true, get: (r) => fmtDuration((r.userEngagementDuration || 0) / Math.max(1, r.activeUsers)) },
      ]} />
      <div className="grid md:grid-cols-3 gap-4 mt-4">
        <Bars title="Traffic sources" icon={Share2} unit="sessions" testid="ga-sources" items={d.sources.map((s) => ({ name: s.sessionDefaultChannelGroup, count: n(s.sessions) }))} />
        <Bars title="Countries" icon={Globe2} unit="users" testid="ga-countries" items={d.countries.map((s) => ({ name: s.country, count: n(s.activeUsers) }))} />
        <Bars title="Devices" icon={Monitor} unit="users" testid="ga-devices" items={d.devices.map((s) => ({ name: s.deviceCategory, count: n(s.activeUsers) }))} />
      </div>
    </div>
  );
}
